import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraIconButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { DocumentEditor } from '@onlyoffice/document-editor-react';
import { GripVertical, Loader2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOfficeDocumentDrawer } from '../../contexts/OfficeDocumentDrawerContext';
import { useDrawerResize } from '../../hooks/useDrawerResize';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { useEditSession } from '../../pages/Editor/hooks/useEditSession';
import { DocumentDraftPreview } from './DocumentDraftPreview';
import { DocumentProposalPreview } from './DocumentProposalPreview';
function docEditorInstance(editorId) {
    return window.DocEditor?.instances?.[editorId];
}
/**
 * OnlyOfficeEmbed mounts one shirohige document in an OnlyOffice editor. When
 * shanks applies changes server-side (via Document Builder), it reloads the
 * editor so the freshly-written bytes replace the Document Server's cache.
 */ function OnlyOfficeEmbed({ documentId }) {
    const { data: session, error: sessionError } = useEditSession(documentId);
    const [ready, setReady] = useState(false);
    const config = useMemo(()=>{
        if (!session) return null;
        try {
            return JSON.parse(session.editorConfigJson);
        } catch  {
            return null;
        }
    }, [
        session
    ]);
    const editorId = `office-drawer-editor-${documentId}`;
    // The drawer is closable, so on unmount destroy the OnlyOffice instance —
    // otherwise it lingers in window.DocEditor.instances and a reopen reuses a
    // stale one.
    useEffect(()=>{
        return ()=>{
            try {
                docEditorInstance(editorId)?.destroyEditor?.();
            } catch  {
            // best-effort teardown
            }
        };
    }, [
        editorId
    ]);
    // Reload-on-apply is centralised in useEditSession (above): it refetches this
    // document's session when shanks dispatches `shanks-document-applied`, and the
    // refreshed config's new content-versioned document.key reloads the ONLYOFFICE
    // editor IN PLACE (the wrapper destroys + recreates its own iframe) — we must
    // NOT remount the component ourselves or React's DOM teardown collides with
    // ONLYOFFICE's and throws removeChild.
    const handleDocumentReady = useCallback(()=>{
        setReady(true);
    }, []);
    if (sessionError) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-full items-center justify-center text-sm text-rose-600",
            children: "Failed to load the document editor."
        });
    }
    if (!config || !session) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-full items-center justify-center",
            children: /*#__PURE__*/ _jsx(Loader2, {
                className: "h-6 w-6 animate-spin text-muted-foreground"
            })
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex h-full min-h-0 flex-col",
        children: [
            ready && /*#__PURE__*/ _jsx("span", {
                "data-testid": Testids.assistant.officeConnectorReady,
                className: "sr-only",
                "aria-hidden": true
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "min-h-0 flex-1",
                children: /*#__PURE__*/ _jsx(DocumentEditor, {
                    id: editorId,
                    documentServerUrl: session.documentServerUrl,
                    config: config,
                    events_onDocumentReady: handleDocumentReady
                })
            })
        ]
    });
}
/**
 * A resizable right-side drawer that hosts an OnlyOffice editor for a
 * standalone document created from chat. Mounted once per tree; it renders
 * nothing until openOffice() is called.
 *
 * `overlay` is for hosts with no flex row to sit in (a scrolling page like
 * Documents): the drawer pins to the viewport's right edge over a backdrop
 * instead of landing in-flow at the bottom of the page.
 */ export function OfficeDocumentDrawer({ overlay = false } = {}) {
    const { isOpen, documentId, title, close, proposals, previewOnly, docType } = useOfficeDocumentDrawer();
    const [showCurrent, setShowCurrent] = useState(false);
    const [selectedProposalId, setSelectedProposalId] = useState(null);
    const pending = Object.entries(proposals ?? {}).filter(([, item])=>item.documentId === documentId);
    const activeProposalId = pending.some(([id])=>id === selectedProposalId) ? selectedProposalId : pending.at(-1)?.[0];
    const activeProposal = activeProposalId ? proposals[activeProposalId] : undefined;
    const proposal = activeProposal?.operations && activeProposal.operations.length > 0 ? activeProposal.operations : undefined;
    const draft = activeProposal?.draft;
    useEffect(()=>{
        setShowCurrent(false);
    }, [
        documentId,
        proposal
    ]);
    const [width, setWidth] = useState(640);
    const resizeHandleRef = useDrawerResize({
        minWidth: 360,
        maxWidth: 1400,
        defaultWidth: 640,
        onWidthChange: setWidth
    });
    const setSidebarCollapsed = useLayoutStore((s)=>s.setSidebarCollapsed);
    const setChatRailCollapsed = useLayoutStore((s)=>s.setChatRailCollapsed);
    // Editing a document is a focus task: fold the app rail and the chat list so
    // the editor gets real width, and restore both when it closes. Only restore
    // what this pane folded — a rail the user had already collapsed by hand
    // stays collapsed.
    const foldedRef = useRef(null);
    useEffect(()=>{
        if (!isOpen) return;
        const { sidebarCollapsed, chatRailCollapsed } = useLayoutStore.getState();
        foldedRef.current = {
            sidebar: !sidebarCollapsed,
            rail: !chatRailCollapsed
        };
        setSidebarCollapsed(true);
        setChatRailCollapsed(true);
        return ()=>{
            const folded = foldedRef.current;
            if (folded?.sidebar) setSidebarCollapsed(false);
            if (folded?.rail) setChatRailCollapsed(false);
            foldedRef.current = null;
        };
    }, [
        isOpen,
        setSidebarCollapsed,
        setChatRailCollapsed
    ]);
    // Overlay is a modal: focus moves in on open and back to whatever opened it
    // on close.
    const dialogRef = useRef(null);
    useEffect(()=>{
        if (!isOpen || !overlay) return;
        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        dialogRef.current?.querySelector(`[data-testid="${Testids.assistant.officeDocumentDrawerClose}"]`)?.focus();
        return ()=>opener?.focus();
    }, [
        isOpen,
        overlay
    ]);
    // Escape closes; Tab cycles within the dialog. Keys typed inside the
    // OnlyOffice iframe never reach this window, so the editor keeps its own.
    useEffect(()=>{
        if (!isOpen || !overlay) return;
        const onKeyDown = (e)=>{
            if (e.key === 'Escape') {
                close();
                return;
            }
            const dialog = dialogRef.current;
            if (e.key !== 'Tab' || !dialog) return;
            const focusables = Array.from(dialog.querySelectorAll('button:not([disabled]), select, iframe, [tabindex]:not([tabindex="-1"])'));
            const first = focusables[0];
            const last = focusables.at(-1);
            if (!first || !last) return;
            const active = document.activeElement;
            if (!dialog.contains(active)) {
                e.preventDefault();
                first.focus();
            } else if (e.shiftKey && active === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return ()=>window.removeEventListener('keydown', onKeyDown);
    }, [
        isOpen,
        overlay,
        close
    ]);
    if (!isOpen || !documentId) return null;
    const drawer = // In chat: in-flow, a flex sibling of the thread so opening the editor
    // reflows (shrinks) the conversation instead of floating over it — the same
    // shape as the Assist rail.
    /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.assistant.officeDocumentDrawer,
        className: overlay ? 'fixed inset-y-0 right-0 z-40 flex flex-col border-l border-border bg-card pl-6 shadow-xl' : 'relative flex min-h-0 flex-shrink-0 flex-col border-l border-border bg-card pl-6',
        style: {
            width,
            maxWidth: overlay ? '90vw' : '60%'
        },
        children: [
            /*#__PURE__*/ _jsxs("button", {
                ref: resizeHandleRef,
                type: "button",
                "aria-label": "Resize document editor",
                title: "Drag left or right to resize",
                className: "absolute inset-y-0 left-0 z-10 flex w-6 cursor-col-resize select-none flex-col items-center justify-center gap-3 border-r border-border bg-muted text-muted-foreground transition-colors hover:bg-border hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring active:bg-border-strong",
                children: [
                    /*#__PURE__*/ _jsx(GripVertical, {
                        className: "h-5 w-5",
                        "aria-hidden": true
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        "aria-hidden": true,
                        className: "text-[length:var(--fs-ui-meta)] font-medium",
                        style: {
                            writingMode: 'vertical-rl'
                        },
                        children: "Drag to resize"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-3 border-b border-border px-4 py-2",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "flex-1 truncate text-sm font-medium text-foreground",
                        title: title,
                        children: title
                    }),
                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                        size: "sm",
                        variant: "ghost",
                        label: "Close document editor",
                        onClick: close,
                        "data-testid": Testids.assistant.officeDocumentDrawerClose,
                        children: /*#__PURE__*/ _jsx(X, {
                            className: "h-4 w-4"
                        })
                    })
                ]
            }),
            pending.length > 1 && /*#__PURE__*/ _jsxs("label", {
                className: "px-4 py-2 text-sm",
                children: [
                    "Pending proposal",
                    /*#__PURE__*/ _jsx("select", {
                        "aria-label": "Pending proposal",
                        value: activeProposalId ?? '',
                        onChange: (event)=>setSelectedProposalId(event.target.value),
                        className: "ml-2 rounded border border-border bg-background p-1",
                        children: pending.map(([id], index)=>/*#__PURE__*/ _jsxs("option", {
                                value: id,
                                children: [
                                    "Proposal ",
                                    index + 1
                                ]
                            }, id))
                    })
                ]
            }),
            proposal && !previewOnly && /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-2 border-b border-border p-2 text-sm",
                children: [
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        "aria-pressed": !showCurrent,
                        className: "rounded px-3 py-1 hover:bg-muted",
                        onClick: ()=>setShowCurrent(false),
                        children: "Draft preview"
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        "aria-pressed": showCurrent,
                        className: "rounded px-3 py-1 hover:bg-muted",
                        onClick: ()=>setShowCurrent(true),
                        children: "Current document"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "min-h-0 flex-1",
                children: draft && (previewOnly || !showCurrent) && docType ? /*#__PURE__*/ _jsx(DocumentDraftPreview, {
                    documentType: docType,
                    source: draft
                }) : proposal && (previewOnly || !showCurrent) ? /*#__PURE__*/ _jsx(DocumentProposalPreview, {
                    operations: proposal
                }) : /*#__PURE__*/ _jsx(OnlyOfficeEmbed, {
                    documentId: documentId
                }, documentId)
            })
        ]
    });
    if (!overlay) return drawer;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.assistant.officeDocumentDrawerBackdrop,
                className: "fixed inset-0 z-30 bg-[var(--scrim)]",
                onClick: close,
                "aria-hidden": "true"
            }),
            /*#__PURE__*/ _jsx("div", {
                ref: dialogRef,
                role: "dialog",
                "aria-modal": "true",
                "aria-label": title,
                children: drawer
            })
        ]
    });
}
